// Lo que dice el audio que está sonando, leído de la propia emisión.
//
// Contrato: `EMISORA_WEB_INSTRUCCIONES.md` § `/emisora/onair.json`, apartado
// «Cuándo pedirlo y cuándo cambiar lo que se ve».
//
// 🔴 **El problema que resuelve: la pantalla seguía al servidor, y nadie oye
// al servidor.** La emisión HLS llega a cada aparato con un colchón de entre
// 12 y más de medio minuto, según la red. Con la ficha atada al reloj del
// servidor no hay número que acierte:
//
//   · Si la detección es inmediata, la ficha cambia **antes** de que se oiga
//     la canción nueva.
//   · Si tarda, cambia **después**.
//
// Y el colchón es distinto en cada móvil y en cada wifi, así que no se puede
// compensar con una constante. Medido el 25.09.26 sobre una grabación de
// Pablo: la ficha cambió 43 s después de que el servidor pasara de página.
//
// **La emisión ya trae la respuesta.** Cada segmento HLS lleva metadatos ID3
// temporizados con artista (`TPE1`), título (`TIT2`) y disco (`TALB`). Van
// pegados al audio, así que cambian **cuando suena el cambio**, tenga cada
// oyente el retraso que tenga. Comprobado bajando un segmento el 25.09.26:
// decía «The Smashing Pumpkins · Rocket · Siamese Dream», lo mismo que el
// servidor en ese instante.
//
// ⚠️ **Un solo camino para los dos motores, y no es casualidad.** El contrato
// menciona hls.js y el HLS nativo de Safari por separado, y la primera idea
// fue montar dos lectores. No hace falta: **los dos publican los ID3 como
// `cue`s en una pista de texto del propio `<audio>`**, y las `cue`s se
// disparan justo cuando ese audio suena. Escuchando ahí, iPhone y Android
// comparten implementación. Y es lo correcto además de lo cómodo: el evento de
// hls.js entrega el fragmento **al analizarlo**, que es hasta un minuto antes
// de que se oiga; la `cue` no.
//
// ⚠️ Esto **no detecta la canción, la reconoce**: dice qué suena, y quien
// escucha busca ese artista y ese título en lo que `onair.json` ya trajo. Si
// no está —muy raro: solo con un retraso enorme—, se vuelve a pedir.

import { ref } from "vue";

/**
 * Lo último que dijo el audio. `null` mientras no haya sonado nada.
 *
 * `{ artista, titulo, disco }`
 */
const loQueSuena = ref(null);

/** Cuándo llegó la última `cue` con datos, sea o no un cambio. */
let ultimaLectura = 0;

/**
 * Si el audio está mandando ID3 ahora mismo: alguna `cue` con datos en los
 * últimos `ms`. Las `cue`s se repiten con cada fragmento, así que mientras
 * suena la emisión llegan cada pocos segundos; en pausa o sin audio, no.
 */
export function id3Vivo(ms = 30000) {
  return !!ultimaLectura && Date.now() - ultimaLectura < ms;
}

/** El elemento al que estamos enganchados, para no engancharnos dos veces. */
let enganchado = null;
let soltar = null;

/** Las etiquetas que nos interesan, con el nombre que usamos dentro. */
const ETIQUETAS = { TPE1: "artista", TIT2: "titulo", TALB: "disco" };

/**
 * Saca el texto de una `cue` de ID3.
 *
 * Los dos motores la entregan con formas parecidas pero no idénticas, y ni
 * siquiera una sola versión de hls.js es consistente: unas veces el texto está
 * en `value.data` y otras en `value.info`. Se prueban por orden en vez de
 * elegir según el navegador, que es lo que se rompe con la siguiente versión.
 */
function textoDe(cue) {
  const v = cue?.value;
  if (!v) return null;
  const clave = v.key || v.info;
  const campo = ETIQUETAS[clave];
  if (!campo) return null;

  const texto = typeof v.data === "string" ? v.data : v.info;
  if (typeof texto !== "string" || !texto.trim()) return null;

  return { campo, texto: texto.trim() };
}

/**
 * Lee las `cue`s activas de una pista y publica lo que digan.
 *
 * Se leen **todas las activas a la vez** y no de una en una: el artista, el
 * título y el disco llegan en tres `cue`s distintas del mismo instante, y
 * publicarlas por separado pintaría medio segundo el artista nuevo con el
 * título viejo.
 */
function leePista(pista) {
  const activas = pista.activeCues;
  if (!activas || !activas.length) return;

  const leido = {};
  for (const cue of activas) {
    const d = textoDe(cue);
    if (d) leido[d.campo] = d.texto;
  }

  if (!leido.artista && !leido.titulo) return;
  ultimaLectura = Date.now();

  const antes = loQueSuena.value;
  if (
    antes &&
    antes.artista === (leido.artista || "") &&
    antes.titulo === (leido.titulo || "")
  ) {
    return;
  }

  loQueSuena.value = {
    artista: leido.artista || "",
    titulo: leido.titulo || "",
    disco: leido.disco || "",
  };
}

/**
 * Engancha el lector al elemento de audio de la emisión.
 *
 * ⚠️ **Las pistas aparecen tarde.** Ninguno de los dos motores las tiene al
 * montar el elemento: hls.js la crea al analizar el primer fragmento con ID3 y
 * Safari al abrir el stream. Por eso se escucha `addtrack` además de recorrer
 * las que ya haya; mirar una sola vez al principio no encuentra nada.
 *
 * ⚠️ Y la pista hay que ponerla en `hidden`. En `disabled` —que es como nacen
 * algunas— el navegador no dispara `cuechange`, así que se escucharía un
 * silencio que parece que no hay metadatos. `hidden` procesa las `cue`s sin
 * intentar pintarlas, que es justo lo que queremos.
 */
export function escuchaLaEmision(audio) {
  if (!audio || audio === enganchado) return;

  dejaDeEscuchar();
  enganchado = audio;

  const limpiezas = [];

  const engancha = (pista) => {
    if (!pista || pista.kind !== "metadata") return;
    pista.mode = "hidden";
    const alCambiar = () => leePista(pista);
    pista.addEventListener("cuechange", alCambiar);
    limpiezas.push(() => pista.removeEventListener("cuechange", alCambiar));
    // Por si ya hay una activa en el momento de engancharse.
    leePista(pista);
  };

  for (const pista of audio.textTracks || []) engancha(pista);

  const alAñadir = (e) => engancha(e.track);
  audio.textTracks?.addEventListener?.("addtrack", alAñadir);
  limpiezas.push(() =>
    audio.textTracks?.removeEventListener?.("addtrack", alAñadir),
  );

  soltar = () => limpiezas.forEach((f) => f());
}

export function dejaDeEscuchar() {
  if (soltar) soltar();
  soltar = null;
  enganchado = null;
}

export function useId3Emision() {
  return { loQueSuena, escuchaLaEmision, dejaDeEscuchar };
}
